/**
 * Señales de sesión del navegador para la telemetría 3.1:
 * - `heartbeat` cada 30 s, solo con la pestaña visible (y se pausa si lleva
 *   30 min sin tocar nada, para que una pestaña olvidada no mande latidos toda
 *   la noche; vuelve en cuanto hay interacción). El tiempo activo de cada
 *   latido lo lleva el tracker con las interacciones que le pasa este módulo;
 * - `tab_hidden` / `tab_visible` (al ocultarse, lo pendiente sale con beacon);
 * - `exit_intent_detected` en escritorio cuando el ratón sale por arriba (una
 *   vez por paso, eso lo controla el tracker);
 * - `page_exited` al salir de la página (`pagehide`) y `page_loaded` otra vez
 *   si vuelve de la caché de atrás/adelante (`pageshow` con `persisted`).
 */
import { NAVIGATION_TYPES, NavigationType } from "./events";

export interface SessionWatcherTarget {
  heartbeat: () => void;
  /** Interacción con la pestaña visible (cuenta para el tiempo activo). */
  interaction: () => void;
  tabHidden: () => void;
  tabVisible: () => void;
  exitIntent: () => void;
  pageExited: () => void;
  pageRestored: () => void;
}

export interface SessionWatcherOptions {
  heartbeatMs?: number;
  /** Sin interacción durante más que esto, los latidos se pausan. */
  heartbeatMaxIdleMs?: number;
  isDesktop?: () => boolean;
  now?: () => number;
  doc?: Document;
  win?: Window;
}

export const HEARTBEAT_MS = 30_000;
export const HEARTBEAT_MAX_IDLE_MS = 30 * 60_000;

const INTERACTION_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "wheel",
] as const;

/** Escritorio: pantalla ancha y puntero fino (ratón). */
export const isDesktopDevice = (win: Window = window): boolean => {
  try {
    if (win.innerWidth < 1024) return false;
    if (typeof win.matchMedia === "function") {
      return win.matchMedia("(pointer: fine)").matches;
    }
    return true;
  } catch {
    return false;
  }
};

/** Tipo de navegación de esta carga de página, si el navegador lo dice. */
export const navigationTypeOf = (
  perf: Pick<Performance, "getEntriesByType"> | undefined = typeof performance !== "undefined"
    ? performance
    : undefined
): NavigationType | null => {
  try {
    const entry = perf?.getEntriesByType?.("navigation")?.[0] as
      | { type?: unknown }
      | undefined;
    const type = entry?.type;
    return typeof type === "string" && (NAVIGATION_TYPES as readonly string[]).indexOf(type) >= 0
      ? (type as NavigationType)
      : null;
  } catch {
    return null;
  }
};

export const installSessionWatchers = (
  target: SessionWatcherTarget,
  options: SessionWatcherOptions = {}
): (() => void) => {
  const doc = options.doc ?? document;
  const win = options.win ?? window;
  const heartbeatMs = options.heartbeatMs ?? HEARTBEAT_MS;
  const maxIdleMs = options.heartbeatMaxIdleMs ?? HEARTBEAT_MAX_IDLE_MS;
  const isDesktop = options.isDesktop ?? (() => isDesktopDevice(win));
  const now = options.now ?? (() => Date.now());

  const visible = () => doc.visibilityState !== "hidden";

  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let lastInteraction = now();
  let exited = false;

  const startHeartbeat = () => {
    if (heartbeatTimer) return;
    heartbeatTimer = setInterval(() => {
      if (!visible()) return;
      if (now() - lastInteraction > maxIdleMs) return;
      target.heartbeat();
    }, heartbeatMs);
  };

  const stopHeartbeat = () => {
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  };

  const onInteraction = () => {
    lastInteraction = now();
    if (visible()) target.interaction();
  };

  const onVisibility = () => {
    if (visible()) {
      target.tabVisible();
      lastInteraction = now();
      startHeartbeat();
    } else {
      target.tabHidden();
      stopHeartbeat();
    }
  };

  const onPageHide = () => {
    if (exited) return;
    exited = true;
    target.pageExited();
  };

  const onPageShow = (event: PageTransitionEvent) => {
    // Vuelta desde la caché de atrás/adelante: la página sigue viva.
    if (!event.persisted) return;
    exited = false;
    target.pageRestored();
  };

  const onMouseOut = (event: MouseEvent) => {
    if (event.relatedTarget) return;
    if (event.clientY > 0) return;
    if (!isDesktop()) return;
    target.exitIntent();
  };

  INTERACTION_EVENTS.forEach((name) =>
    win.addEventListener(name, onInteraction, { passive: true, capture: true })
  );
  doc.addEventListener("visibilitychange", onVisibility);
  doc.addEventListener("mouseout", onMouseOut);
  win.addEventListener("pagehide", onPageHide);
  win.addEventListener("pageshow", onPageShow);

  if (visible()) startHeartbeat();

  return () => {
    stopHeartbeat();
    INTERACTION_EVENTS.forEach((name) =>
      win.removeEventListener(name, onInteraction, { capture: true })
    );
    doc.removeEventListener("visibilitychange", onVisibility);
    doc.removeEventListener("mouseout", onMouseOut);
    win.removeEventListener("pagehide", onPageHide);
    win.removeEventListener("pageshow", onPageShow);
  };
};

/** Botón de contacto según el enlace pulsado (llamar, escribir, WhatsApp). */
export const ctaForHref = (
  href: string | null | undefined
): "call" | "email" | "whatsapp" | null => {
  const h = (href || "").trim().toLowerCase();
  if (h.startsWith("tel:")) return "call";
  if (h.startsWith("mailto:")) return "email";
  if (
    h.startsWith("whatsapp:") ||
    h.includes("wa.me/") ||
    h.includes("api.whatsapp.com") ||
    h.includes("web.whatsapp.com")
  ) {
    return "whatsapp";
  }
  return null;
};
